import { ReactNode, useCallback, useMemo, useState } from 'react';

import {
  type Checkout,
  type ConsumerCheckoutCartsResponse,
  useCompleteCheckout,
  useShowConsumerCheckout,
} from '@givebacks/api/store';
import { useFormyValidator } from '@givebacks/formy';
import { useIsMutating } from '@tanstack/react-query';
import { ICredential } from 'core/Api';
import { getErrorMessage_v2 } from 'core/Utils';

import { CheckoutForms } from '../checkout.types';
import { getCartIdsFromCheckout } from '../utils';
import { useCreateCredential } from './useCreateCredential';

const useIsCheckoutLoading = () => {
  const isCheckoutMutation = useIsMutating({ mutationKey: ['checkout'] });
  return useMemo(() => isCheckoutMutation > 0, [isCheckoutMutation]);
};

const useGetIsPaymentMethodNeeded = ({ checkoutId }: { checkoutId: string }) => {
  const { data: isPaymentMethodNeeded } = useShowConsumerCheckout({
    checkoutId,
    select: useCallback(
      (data: ConsumerCheckoutCartsResponse) => data.checkout.payment_method_needed,
      []
    ),
  });
  return { isPaymentMethodNeeded: isPaymentMethodNeeded };
};

const useGetCartIdsFromCheckout = ({ checkoutId }: { checkoutId: string }) => {
  const { data: cartIds } = useShowConsumerCheckout({
    checkoutId: checkoutId,
    select: getCartIdsFromCheckout,
  });
  return useMemo(() => cartIds ?? [], [cartIds]);
};

const useCheckoutForm = ({
  checkoutId,
  onSuccess,
}: {
  checkoutId: string;
  onSuccess?: (cart: Checkout) => void;
}) => {
  const isCheckoutLoading = useIsCheckoutLoading();

  const [error, setError] = useState<ReactNode | undefined>();

  const { createCredential, isCreateCredentialLoading } = useCreateCredential({
    onCredentialChange: useCallback(() => {
      /** Reset errors on credential change  */
      setError(undefined);
    }, []),
  });

  const { isPaymentMethodNeeded } = useGetIsPaymentMethodNeeded({ checkoutId });
  const cartIds = useGetCartIdsFromCheckout({ checkoutId });

  const { mutate: createCheckout, isPending: isLoading } = useCompleteCheckout();

  const { validateForms, useGetForms, useGetFormById, useFormScroll } =
    useFormyValidator<CheckoutForms>();

  const forms = useGetForms();

  const getFormById = useGetFormById();

  const { scrollToForm } = useFormScroll('shop_to_give_enrollment', {
    offset: 0,
    duration: 500,
  });

  const createCheckoutCart = useCallback(
    (stripeCredential?: ICredential) => {
      const anonymous = forms.anonymous?.values?.anonymous;
      const userInfo = forms.donor_info?.values;

      const checkoutAdditionalFields = forms.checkout_additional_fields?.values?.carts ?? {};
      const addressFields = forms.additional_fields_address?.getTransformedValues();

      const shopToGiveEnrollment = forms.shop_to_give_enrollment?.values?.terms;
      const keepMeUpdated = forms.keep_me_updated?.values?.keep_me_updated;

      const checkoutRequest = {
        ...userInfo,
        anonymous,
        mailing_address: addressFields?.options.asObject['Mailing Address'],
        options: {
          ...checkoutAdditionalFields,
        },
        data_points: {
          enrolled_in_shop_to_give: shopToGiveEnrollment,
          keep_me_updated: keepMeUpdated,
        },
      };

      // Guards
      if (!userInfo) return false;

      createCheckout(
        {
          checkout: checkoutRequest,
          checkoutId: checkoutId,
          cartIds: cartIds,
          token: stripeCredential?.stripe_payment_method?.uuid,
          recaptcha: forms?.recaptcha?.values?.recaptcha ?? null,
        },
        {
          onSuccess(data) {
            onSuccess?.(data.checkout);
          },
          onError(e: Error) {
            const error = e as { response?: { data?: { error?: string } } };
            const message = getErrorMessage_v2(error);
            setError(message);
          },
        }
      );
    },
    [
      cartIds,
      checkoutId,
      createCheckout,
      forms.additional_fields_address,
      forms.anonymous?.values?.anonymous,
      forms.checkout_additional_fields?.values?.carts,
      forms.donor_info?.values,
      forms.keep_me_updated?.values?.keep_me_updated,
      forms?.recaptcha?.values?.recaptcha,
      forms.shop_to_give_enrollment?.values?.terms,
      onSuccess,
    ]
  );

  const checkout = useCallback(async () => {
    // Run form validation on all mounted forms
    validateForms();

    try {
      // Check for credential, this will will return a stripe credential or throw an error relevant to the tokenization process
      const credential = isPaymentMethodNeeded ? await createCredential() : undefined;

      // If credential was valid but our forms are still missing error, throw an error
      if (validateForms().isValid == false) throw new Error('Missing required fields');

      createCheckoutCart(credential);
    } catch (e) {
      const error = e as { response: { data: { error: string } } };
      const message = getErrorMessage_v2(error);
      setError(error?.response?.data?.error ?? message ?? '');

      const stgForm = getFormById('shop_to_give_enrollment');
      const hasErrors = Object.keys(stgForm?.errors ?? {}).length > 0;

      if (hasErrors) {
        scrollToForm();
      }
    }
  }, [
    validateForms,
    isPaymentMethodNeeded,
    createCredential,
    createCheckoutCart,
    getFormById,
    scrollToForm,
  ]);
  const isCheckoutFormLoading = useMemo(
    () => isCheckoutLoading || isCreateCredentialLoading || isLoading,
    [isCheckoutLoading, isCreateCredentialLoading, isLoading]
  );

  return {
    isCheckoutFormLoading,
    error,
    checkout,
  };
};

export { useCheckoutForm };
